/**
 * PIN rules and client-side KDF parameters, per docs/ARCHITECTURE.md §5.
 *
 * The PIN itself never leaves the device. The browser (or the test harness)
 * runs PBKDF2 and sends only the derived key.
 */

export const PIN_LENGTH = 6;
export const CLIENT_PBKDF2_ITERATIONS = 210_000;
export const CLIENT_PBKDF2_SALT_PREFIX = "vocifuchs:";
export const DK_BYTES = 32;

/**
 * Server-side PBKDF2 iterations per kdf_version.
 *
 * v1 (10_000) was chosen when the account was on the free tier and the whole
 * invocation had to fit in 10 ms of CPU. Measured in workerd, the real cost is
 * ~0.06 ms per 1_000 iterations, so v1 costs ~0.6 ms — the original 6 ms
 * estimate in docs/ARCHITECTURE.md §5 was an order of magnitude too high.
 *
 * v2 (100_000, ~6 ms) is the **platform ceiling**, not a tuning choice. The
 * deployed Workers runtime rejects PBKDF2 above 100_000 iterations with
 * `NotSupportedError`. Local workerd does NOT enforce this, so a higher value
 * passes every local test and then fails in production — verified the hard way
 * against staging on 09.09.2026.
 *
 * It still matters: a 6-digit PIN has only 10^6 candidates, and an attacker
 * holding the database must run the client KDF (210_000) plus the server KDF
 * per guess. Throttling remains the real defence.
 *
 * Accounts carry their own kdf_version and are re-hashed on next login.
 */
export const SERVER_PBKDF2_ITERATIONS_V1 = 10_000;

/** Hard limit enforced by the deployed Workers runtime. Do not exceed. */
export const MAX_WORKERS_PBKDF2_ITERATIONS = 100_000;
export const SERVER_PBKDF2_ITERATIONS_V2 = MAX_WORKERS_PBKDF2_ITERATIONS;
export const SERVER_SALT_BYTES = 16;
export const CURRENT_KDF_VERSION = 2;

/** Iterations for a stored kdf_version, or null if we do not know it. */
export function serverIterations(kdfVersion: number): number | null {
  if (kdfVersion === 1) return SERVER_PBKDF2_ITERATIONS_V1;
  if (kdfVersion === 2) return SERVER_PBKDF2_ITERATIONS_V2;
  return null;
}

/** True when a successful login should transparently upgrade the stored hash. */
export function needsRehash(kdfVersion: number): boolean {
  return kdfVersion !== CURRENT_KDF_VERSION;
}

const ALL_SAME = /^(\d)\1{5}$/;
const SEQUENTIAL = new Set([
  "012345",
  "123456",
  "234567",
  "345678",
  "456789",
  "567890",
  "987654",
  "876543",
  "765432",
  "654321",
  "543210",
]);

/**
 * Years a child in this product's age range (or a sibling) might use as a PIN.
 * 2010–2018 covers 8–16 year olds in 2026, plus a little margin.
 */
const YEAR_PIN = /^201[0-8]\d{2}$/;

export function isPinShape(pin: string): boolean {
  return /^\d{6}$/.test(pin);
}

/** True when the PIN is too guessable to store. */
export function isWeakPin(pin: string): boolean {
  if (!isPinShape(pin)) return true;
  if (ALL_SAME.test(pin)) return true;
  if (SEQUENTIAL.has(pin)) return true;
  if (YEAR_PIN.test(pin)) return true;
  return false;
}

export function clientKdfSalt(handle: string): string {
  return `${CLIENT_PBKDF2_SALT_PREFIX}${handle}`;
}

/**
 * PBKDF2-SHA256 of the PIN, matching ARCHITECTURE §5.
 * Works in the browser and in the Worker (WebCrypto).
 */
export async function deriveClientKey(pin: string, handle: string): Promise<string> {
  const bytes = new TextEncoder().encode(pin);
  const salt = new TextEncoder().encode(clientKdfSalt(handle));
  const key = await crypto.subtle.importKey("raw", bytes, "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt,
      iterations: CLIENT_PBKDF2_ITERATIONS,
    },
    key,
    DK_BYTES * 8,
  );
  return bytesToB64(new Uint8Array(bits));
}

export function bytesToB64(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

export function b64ToBytes(value: string): Uint8Array {
  const binary = atob(value);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i);
  return out;
}
